import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';

const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const GOOGLE_TOKEN_URL = 'https://oauth2.googleapis.com/token';
// Narrowest scope that covers our usage — see AuthService.ts CALENDAR_SCOPE note.
const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.events.readonly';
const SCOPES = `openid email ${CALENDAR_SCOPE}`;

export interface IosOAuthResult {
  accessToken: string;
  refreshToken: string;
  expiresInSec: number;
  email?: string;
}

function getIosClientId(): string {
  const clientId = import.meta.env.VITE_GOOGLE_OAUTH_IOS_CLIENT_ID;
  if (!clientId) throw new Error('VITE_GOOGLE_OAUTH_IOS_CLIENT_ID is not configured');
  return clientId;
}

function buildRedirectUri(clientId: string): string {
  const reversed = clientId.split('.').reverse().join('.');
  return `${reversed}:/oauthredirect`;
}

function base64UrlDecode(str: string): string {
  const padded = str.replace(/-/g, '+').replace(/_/g, '/');
  return atob(padded);
}

function decodeIdTokenEmail(idToken: string | undefined): string | undefined {
  if (!idToken) return undefined;
  const parts = idToken.split('.');
  if (parts.length !== 3 || !parts[1]) return undefined;
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1])) as { email?: unknown };
    return typeof payload.email === 'string' ? payload.email : undefined;
  } catch {
    return undefined;
  }
}

interface TokenResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  id_token?: string;
  error?: string;
  error_description?: string;
}

async function exchangeCodeClientSide(
  code: string,
  clientId: string,
  redirectUri: string,
): Promise<IosOAuthResult> {
  const resp = await fetch(GOOGLE_TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      code,
      client_id: clientId,
      redirect_uri: redirectUri,
      grant_type: 'authorization_code',
    }),
  });

  const data = (await resp.json()) as TokenResponse;
  if (!resp.ok || data.error) {
    throw new Error(
      `Token exchange failed: ${data.error_description ?? data.error ?? resp.status}`,
    );
  }
  if (!data.access_token || !data.refresh_token || typeof data.expires_in !== 'number') {
    throw new Error('Token exchange returned incomplete tokens');
  }

  const email = decodeIdTokenEmail(data.id_token);
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresInSec: data.expires_in,
    ...(email !== undefined && { email }),
  };
}

function waitForAuthCode(redirectUri: string): Promise<string> {
  let resolveFlow!: (code: string) => void;
  let rejectFlow!: (err: Error) => void;

  const flowPromise = new Promise<string>((res, rej) => {
    resolveFlow = res;
    rejectFlow = rej;
  });

  let settled = false;

  const appListenerPromise = App.addListener('appUrlOpen', (event) => {
    if (!event.url.startsWith(redirectUri)) return;
    if (settled) return;
    settled = true;

    const callbackUrl = new URL(event.url);
    const error = callbackUrl.searchParams.get('error');
    const code = callbackUrl.searchParams.get('code');

    void Promise.all([appListenerPromise, browserListenerPromise]).then(
      async ([appHandle, browserHandle]) => {
        await appHandle.remove();
        await browserHandle.remove();
        void Browser.close();

        if (error) {
          rejectFlow(new Error(`Google OAuth error: ${error}`));
        } else if (!code) {
          rejectFlow(new Error('OAuth callback contained no code parameter'));
        } else {
          resolveFlow(code);
        }
      },
    );
  });

  const browserListenerPromise = Browser.addListener('browserFinished', () => {
    if (settled) return;
    settled = true;

    void Promise.all([appListenerPromise, browserListenerPromise]).then(
      async ([appHandle, browserHandle]) => {
        await appHandle.remove();
        await browserHandle.remove();
        rejectFlow(new Error('Sign-in cancelled'));
      },
    );
  });

  return flowPromise;
}

export async function startIosOAuthFlow(): Promise<IosOAuthResult> {
  const clientId = getIosClientId();
  const redirectUri = buildRedirectUri(clientId);

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: SCOPES,
    access_type: 'offline',
    prompt: 'consent',
  });

  const authUrl = `${GOOGLE_AUTH_URL}?${params.toString()}`;

  const codePromise = waitForAuthCode(redirectUri);
  void Browser.open({ url: authUrl, presentationStyle: 'popover' });
  const code = await codePromise;

  return exchangeCodeClientSide(code, clientId, redirectUri);
}
